/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { T } from "../../../lib/i18n";

/* 管理员 / CEO：软取消设计项目并保留可恢复记录；取消待审批 / 结构工单，但不改变 CRM 客户或合同状态。 */
export const DeleteProjectDialog = ({ p, api, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (reason.trim().length < 2) return toast.error(T("请填写取消原因（≥2 字）"));
    setBusy(true);
    try {
      const { data } = await api.post(`/design/v3/projects/${p.id}/delete`, { reason: reason.trim() });
      toast.success(T("设计项目已取消{0}", [data.structOrdersCancelled ? T("，已取消 {0} 个结构外包工单", [data.structOrdersCancelled]) : ""]));
      onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="dv3-delete-dialog">
      <div className="bg-white w-full max-w-md p-5 space-y-3 rounded-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-red-700 flex items-center gap-2"><Trash2 className="h-5 w-5" />{T("取消设计项目")}</h3>
        <p className="text-sm text-slate-700"><b>{p.address}</b><span className="text-slate-400"> · {p.projectId}</span></p>
        <ul className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1" data-testid="dv3-delete-effects">
          <li className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />{T("项目从进行中列表移除（数据保留，管理员可在「已移交工程部 → 已删除项目」恢复）")}</li>
          <li>{T("• 该项目未决的移交审批、结构外包工单将自动取消")}</li>
          <li>{T("• CRM 客户和已签合同状态保持不变，后续新合同仍可继续上传并转入设计")}</li>
          <li>{T("• 若关联工程项目仍在设计阶段，将按取消联动配置推进至「1-Pre-construction」或交由工程部人工处理")}</li>
        </ul>
        <label className="block text-xs text-slate-600">{T("取消原因 *")}
          <textarea data-testid="dv3-delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm" placeholder={T("如：关闭旧设计项目，后续新合同另建项目…")} />
        </label>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
          <Button size="sm" variant="outline" data-testid="dv3-delete-cancel" onClick={onClose}>{T("取消")}</Button>
          <Button size="sm" data-testid="dv3-delete-confirm" disabled={busy || reason.trim().length < 2} onClick={run} className="bg-red-600 hover:bg-red-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : T("确认取消项目")}</Button>
        </div>
      </div>
    </div>
  );
};
